import { Spacing } from '@persona-id/design-tokens';
import { forwardRef, ForwardRefRenderFunction, memo, ReactNode, useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';

import HoverEditControls, {
  ControlButtonGroup,
} from 'dashboard/apps/InquiryTemplateEditor/HoverEditControls';
import { getValidStepStatus } from 'dashboard/apps/InquiryTemplateEditor/utils/steps';
import InquiryStatusSticker from 'dashboard/components/InquiryStatusSticker';
import NodeSticker from 'dashboard/components/NodeSticker';
import Tooltip from 'dashboard/components/Tooltip';
import { usePanZoomBoxControls } from 'dashboard/components-lib/PanZoomBox';
import { InquiryStatus } from 'dashboard/models/inquiry';
import { HStack } from 'design-system/components/Stack';
import { isHovered, isNotDisabled } from 'design-system/lib/pseudoClass';
import { TransitionDuration } from 'design-system/variables/transitions';
import useToggle from 'lib/hooks/useToggle';

const HOVER_CONTROLS_MODIFIERS = [
  {
    name: 'offset',
    options: {
      offset: [0, 8],
    },
  },
  {
    name: 'flip',
    enabled: false,
  },
];

const NODE_STICKER_MODIFIERS = [
  {
    name: 'offset',
    options: {
      offset: [0, 4],
    },
  },
  {
    name: 'flip',
    enabled: false,
  },
];

// TODO (eric): replace with PDS popover when available
const NodeStickerTooltip = styled(Tooltip)<{ isTransforming: boolean }>`
  background-color: transparent;
  z-index: 0 !important;
  padding: 0px;
  ${({ isTransforming }) =>
    isTransforming &&
    css`
      visibility: hidden;
    `}
`;

// TODO (eric): replace with PDS popover when available
const HoverControlsTooltip = styled(Tooltip)<{ isTransforming: boolean }>`
  z-index: 0 !important;
  padding: 2px;
  ${({ isTransforming }) =>
    isTransforming &&
    css`
      visibility: hidden;
      ${ControlButtonGroup} {
        opacity: 0;
      }
    `}
`;

const HoverableAreaContainer = styled.div`
  &:after {
    content: '';
    display: block;
    position: absolute;
    width: 100%;
    height: ${Spacing[9]};
  }
`;

const NodeWrapper = styled.div<{
  omitFromRun?: boolean;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  pointer-events: auto;
  transition: opacity ${TransitionDuration.Fast};
  opacity: ${({ omitFromRun }) => (omitFromRun ? 0.25 : 1.0)};
`;

const ContentWrapper = styled.div`
  margin-top: ${Spacing[2]};
  margin-bottom: ${Spacing[2]};
  &[aria-disabled='true'] {
    pointer-events: none;
    & > * {
      cursor: default;
    }
  }
`;

const Container = styled.div<{ hasClickAction?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  height: ${Spacing[6]};
  ${({ hasClickAction }) =>
    hasClickAction &&
    css`
      &${isHovered}${isNotDisabled} {
        cursor: pointer;
      }
    `}
`;

const StickerContainer = styled(HStack)`
  background-color: transparent;
`;

export interface ScreenFlowNodeWrapperProps {
  'data-name': string;
  'data-test': string;
  'data-marketing-id': string;
  node: ReactNode;
  title?: string;
  hoverEditLink?: string;
  editable: boolean;
  selected?: boolean;
  omitFromRun?: boolean;
  withSticker?: boolean;
  stepStatuses?: InquiryStatus[];
  /**
   * maintains sticker size for legibility in zoom contexts,
   * preferred usage is for nodes without existing tooltips
   * or popovers (e.g action sequence node or connector node)
   */
  withStickerPortal?: boolean;
  withHoverControls?: boolean;
  handleStickerClick?: () => void;
  handleNodeClick?: () => void;
  handleEditClick?: () => void;
  handleDeleteClick?: () => void;
  handleCutClick?: () => void;
  handleCopyClick?: () => void;
}

/**
 * This component is responsible for rendering a ScreenFlowNodeWrapper
 * which provides a name sticker and/or hover edit controls to the ITE flow editor nodes
 */
/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
const ScreenFlowNodeWrapperFwdRef: ForwardRefRenderFunction<
  HTMLDivElement,
  ScreenFlowNodeWrapperProps
> = (
  {
    'data-name': dataName,
    'data-test': dataTest,
    'data-marketing-id': dataMarketingId,
    node,
    title,
    hoverEditLink,
    editable,
    selected,
    stepStatuses,
    omitFromRun,
    withSticker,
    withStickerPortal,
    withHoverControls,
    handleStickerClick,
    handleNodeClick,
    handleEditClick,
    handleDeleteClick,
    handleCutClick,
    handleCopyClick,
  }: ScreenFlowNodeWrapperProps,
  forwardedRef: (instance: HTMLDivElement) => void,
) => {
  const panZoomBoxControls = usePanZoomBoxControls();
  const isTransforming = panZoomBoxControls?.isTransforming ?? false;
  const [isHovered, { enable: activate, disable: deactivate }] = useToggle(false);
  const [isStickerHovered, { enable: activateSticker, disable: deactivateSticker }] =
    useToggle(false);
  const shouldDisplayPopover = isHovered || selected;
  const nodeWithHoverControls = (
    <HoverControlsTooltip
      hideArrow
      placement='bottom-start'
      tooltipShown={shouldDisplayPopover}
      isTransforming={isTransforming}
      modifiers={HOVER_CONTROLS_MODIFIERS}
      tooltip={
        withHoverControls &&
        (hoverEditLink ||
          handleEditClick ||
          handleDeleteClick ||
          handleCutClick ||
          handleCopyClick) && (
          <HoverEditControls
            hoverEditLink={hoverEditLink}
            handleEditClick={handleEditClick}
            handleDeleteClick={handleDeleteClick}
            handleCutClick={handleCutClick}
            handleCopyClick={handleCopyClick}
            editable={editable}
          />
        )
      }
    >
      <ContentWrapper
        data-test={dataTest}
        id={`${title}__node-wrapper`}
        aria-disabled={handleNodeClick == null}
        onClick={handleNodeClick}
      >
        {node}
      </ContentWrapper>
    </HoverControlsTooltip>
  );

  const stickerContent = useCallback(
    ({
      title,
      isHovered,
      selected,
      isStickerHovered,
    }: {
      title: string;
      isHovered: boolean;
      selected: boolean;
      isStickerHovered?: boolean;
    }) => {
      const stepStatus = getValidStepStatus(stepStatuses);
      return (
        <StickerContainer>
          <NodeSticker title={title} isActive={isHovered || isStickerHovered} selected={selected} />
          {stepStatus && <InquiryStatusSticker status={stepStatus} />}
        </StickerContainer>
      );
    },
    [stepStatuses],
  );

  const defaultStickerContent = useMemo(() => {
    if (withSticker) {
      if (withStickerPortal) {
        return shouldDisplayPopover
          ? null
          : /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            stickerContent({ title, isHovered, isStickerHovered, selected });
      } else {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        return stickerContent({ title, isHovered, isStickerHovered, selected });
      }
    } else {
      return null;
    }
  }, [
    isHovered,
    isStickerHovered,
    selected,
    stickerContent,
    shouldDisplayPopover,
    title,
    withSticker,
    withStickerPortal,
  ]);

  const handleContainerClick = useCallback(() => {
    handleStickerClick?.();
  }, [handleStickerClick]);

  return (
    <NodeWrapper
      data-name={dataName}
      data-marketing-id={dataMarketingId}
      id={`${dataMarketingId}--${dataName}`}
      ref={forwardedRef}
      omitFromRun={omitFromRun}
    >
      <Container
        id={`${dataMarketingId}--${dataName}__sticker-container`}
        onClick={handleContainerClick}
        hasClickAction={handleStickerClick != null}
        onMouseEnter={activateSticker}
        onMouseLeave={deactivateSticker}
      >
        {defaultStickerContent}
      </Container>
      <NodeStickerTooltip
        hideArrow
        placement='top-start'
        tooltipShown={shouldDisplayPopover}
        isTransforming={isTransforming}
        modifiers={NODE_STICKER_MODIFIERS}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        tooltip={withSticker && withStickerPortal && stickerContent({ title, isHovered, selected })}
      >
        <HoverableAreaContainer onMouseEnter={activate} onMouseLeave={deactivate}>
          {nodeWithHoverControls}
        </HoverableAreaContainer>
      </NodeStickerTooltip>
      {/* empty bottom container used to extend hover area **/}
      <Container />
    </NodeWrapper>
  );
};

const ScreenFlowNodeWrapper = forwardRef(ScreenFlowNodeWrapperFwdRef);
export default memo(ScreenFlowNodeWrapper);
